import React from 'react';

import { UseFloatingReturn } from '@floating-ui/react-dom/src/types';

import { SetIsVisible } from './useClick';

interface HoverProps {
  setIsVisible: SetIsVisible;
  refs: UseFloatingReturn['refs'];
  delay: number;
  enablePersistence: boolean;
}
type Doc = Document & { __tooltipStamp: number };
export const useHover = ({
  refs,
  setIsVisible,
  delay,
  enablePersistence,
}: HoverProps) => {
  const timeout: null | ReturnType<typeof setTimeout> = null;
  const timeoutRef = React.useRef(timeout);
  const mouseInHandler = React.useCallback(() => {
    // regular old popper method
    if (!enablePersistence) {
      timeoutRef.current = setTimeout(() => {
        (document as Doc).__tooltipStamp = Date.now();
        setIsVisible(true);
      }, delay);
      return;
    }

    // there is no stamp (or first time hovering) -> proceed with regular delay
    if (!(document as Doc).__tooltipStamp) {
      timeoutRef.current = setTimeout(() => {
        (document as Doc).__tooltipStamp = Date.now();
        setIsVisible(true);
      }, delay);
      return;
    }

    // if the tooltip stamp is within 1s of last invocation -> delay 0
    if ((document as Doc).__tooltipStamp + 1000 > Date.now()) {
      timeoutRef.current = setTimeout(() => {
        (document as Doc).__tooltipStamp = Date.now(); // for optional persistence
        setIsVisible(true);
      }, 0);
      return;
    } else {
      // make sure that it actually shows it if its < than 1s
      timeoutRef.current = setTimeout(() => {
        (document as Doc).__tooltipStamp = Date.now();
        setIsVisible(true);
      }, delay);
      return;
    }
  }, [delay, enablePersistence, setIsVisible, timeoutRef]);
  const mouseOutHandler = React.useCallback(() => {
    clearTimeout(timeoutRef.current);
    setIsVisible(false);
  }, [setIsVisible]);
  const hoverManager = () => {
    (refs.reference.current as Element).addEventListener(
      'mouseenter',
      mouseInHandler
    );
    (refs.reference.current as Element).addEventListener(
      'mouseleave',
      mouseOutHandler
    );
    (refs.reference.current as Element).addEventListener(
      'click',
      mouseOutHandler
    );
    return () => {
      (refs.reference.current as Element).removeEventListener(
        'mouseenter',
        mouseInHandler
      );
      (refs.reference.current as Element).removeEventListener(
        'mouseleave',
        mouseOutHandler
      );
      (refs.reference.current as Element).removeEventListener(
        'click',
        mouseOutHandler
      );
    };
  };
  return { hoverManager };
};
